import { useState } from "react";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import api, { formatApiErrorDetail } from "@/lib/api";

const SUBJECTS = ["Order Enquiry", "Fragrance Question", "Press & Wholesale", "General"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "Order Enquiry", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message);
      setForm({ name: "", email: "", subject: "Order Enquiry", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-32 md:pt-40 pb-24" data-testid="contact-page">
      <div className="container-lux grid lg:grid-cols-2 gap-16 lg:gap-24">
        <Reveal>
          <p className="label-mono mb-6 text-blood">Contact</p>
          <h1 className="heading-serif text-5xl md:text-6xl mb-8 leading-[0.95]">
            Speak with the house
          </h1>
          <p className="text-base md:text-lg text-ivory/75 font-sans leading-relaxed mb-12 max-w-md">
            For orders, fragrance questions, press or wholesale enquiries, we respond within two working days.
          </p>
          <div className="space-y-6">
            <Detail label="Customer Care" value="care@beautifullydisturbed.com" />
            <Detail label="Press & Wholesale" value="press@beautifullydisturbed.com" />
            <Detail label="Based in" value="The Netherlands · online atelier" />
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <form onSubmit={submit} className="space-y-8" data-testid="contact-form">
            <Field label="Name" testid="contact-name">
              <input
                required
                value={form.name}
                onChange={set("name")}
                data-testid="contact-name-input"
                className="w-full bg-transparent border-b border-mineral/40 py-3 focus:outline-none focus:border-amethyst transition-colors"
              />
            </Field>
            <Field label="Email" testid="contact-email">
              <input
                required
                type="email"
                value={form.email}
                onChange={set("email")}
                data-testid="contact-email-input"
                className="w-full bg-transparent border-b border-mineral/40 py-3 focus:outline-none focus:border-amethyst transition-colors"
              />
            </Field>
            <Field label="Subject" testid="contact-subject">
              <select
                value={form.subject}
                onChange={set("subject")}
                data-testid="contact-subject-select"
                className="w-full bg-charcoal border-b border-mineral/40 py-3 focus:outline-none focus:border-amethyst transition-colors"
              >
                {SUBJECTS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Message" testid="contact-message">
              <textarea
                required
                rows={4}
                value={form.message}
                onChange={set("message")}
                data-testid="contact-message-input"
                className="w-full bg-transparent border-b border-mineral/40 py-3 focus:outline-none focus:border-amethyst transition-colors resize-none"
              />
            </Field>
            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit"
              className="btn-relic border-alabaster bg-alabaster text-charcoal hover:bg-amethyst hover:border-amethyst disabled:opacity-50"
            >
              {loading ? "Sending…" : "Send Inquiry"}
            </button>
          </form>
        </Reveal>
      </div>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div className="border-t border-mineral/20 pt-5">
      <p className="label-mono mb-2">{label}</p>
      <p className="font-serif text-xl">{value}</p>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="label-mono block mb-2">{label}</label>
      {children}
    </div>
  );
}
